import type React from "react";
import { useCallback, useEffect } from "react";
import type { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { useLocation } from "react-router-dom";

import { useOAuthToken } from "../api";
import { useConfigStore } from "../store/store";

interface TelemetryComponentProps {
    appInsights: ApplicationInsights;
}

export const trackAppLoaded = (appInsights: ApplicationInsights, authRequired: boolean): void => {
    appInsights.trackEvent({
        name: "app.loaded",
        properties: {
            authRequired: String(authRequired),
            entryPath: window.location.pathname,
        }
    });
};

/**
 * Emit a feature-usage event to Application Insights. The event name is the
 * feature id (e.g. "documents.export"); a "source" property tags where it
 * came from so events are self-describing for the admin usage dashboard.
 * Safe to call with an undefined appInsights (no-op before it initializes).
 */
export const trackFeatureUsage = (
    appInsights: ApplicationInsights | undefined,
    feature: string,
    properties?: Record<string, string>,
): void => {
    appInsights?.trackEvent({
        name: feature,
        properties: {
            source: "admin-panel",
            ...properties,
        },
    });
};

/**
 * Hook returning a stable tracker bound to the app's Application Insights
 * instance, so instrumenting a control is one line:
 *   const track = useTrackFeature();
 *   ... onClick={() => track("documents.export")}
 */
export const useTrackFeature = () => {
    const appInsights = useConfigStore((state) => state.appInsights);
    return useCallback(
        (feature: string, properties?: Record<string, string>) =>
            trackFeatureUsage(appInsights, feature, properties),
        [appInsights],
    );
};

export const TelemetryUserContext: React.FC<TelemetryComponentProps> = ({ appInsights }) => {
    const userId = useOAuthToken();

    useEffect(() => {
        if (!userId) {
            appInsights.clearAuthenticatedUserContext();
            return;
        }

        appInsights.setAuthenticatedUserContext(userId);

        return () => {
            appInsights.clearAuthenticatedUserContext();
        };
    }, [appInsights, userId]);

    return null;
};

export const RouteTelemetry: React.FC<TelemetryComponentProps> = ({ appInsights }) => {
    const location = useLocation();

    useEffect(() => {
        appInsights.trackPageView({
            name: location.pathname === "/" ? "home" : location.pathname,
            uri: `${window.location.origin}${location.pathname}${location.hash}`,
            properties: {
                routePath: location.pathname,
            }
        });
    }, [appInsights, location.hash, location.pathname]);

    return null;
};
